import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, ViewEncapsulation } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { AutoFocusModule } from 'primeng/autofocus';
import { BadgeSeverity } from '../fcp-badge';

@Component({
    selector: 'fcp-button',
    standalone: true,
    imports: [CommonModule, ButtonModule, AutoFocusModule],
    styleUrl: './fcp-button.component.less',
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.data-e2e-hook]': 'e2e()'
    },
    template: `<p-button
        [type]="type()"
        [iconPos]="buttonIconPosition()"
        [icon]="icon()"
        [badge]="badge()"
        [label]="label()"
        [disabled]="disabled()"
        [loading]="loading()"
        [loadingIcon]="loadingIcon()"
        [raised]="shouldApplyRaised()"
        [rounded]="rounded()"
        [text]="text()"
        [plain]="plain()"
        [severity]="buttonSeverity()"
        [outlined]="outlined()"
        [link]="linkAppearance()"
        [tabindex]="tabindex()"
        [size]="buttonSize()"
        [variant]="variant()"
        [style]="style()"
        [styleClass]="styleClass()"
        [badgeSeverity]="badgeSeverity()"
        [ariaLabel]="ariaLabel()"
        [pAutoFocus]="autofocus()"
        [fluid]="fluid()"
        [buttonProps]="buttonProps()"
        (onClick)="onClick.emit($event)"
        (onFocus)="onFocus.emit($event)"
        (onBlur)="onBlur.emit($event)"
    />`
})
export class FcpButtonComponent {
    e2e = input<string | null>(null);
    type = input<ButtonType>(ButtonType.Button);
    icon = input<string | undefined>();
    badge = input<string | undefined>();
    label = input<string | undefined>();
    disabled = input<boolean>(false);
    loading = input<boolean>(false);
    loadingIcon = input<string | undefined>();
    raised = input<boolean>(false);
    rounded = input<boolean>(false);
    plain = input<boolean>(false);
    link = input<boolean>(false);
    tabindex = input<number | undefined>();
    variant = input<ButtonVariant | undefined>();
    style = input<unknown>();
    styleClass = input<string | undefined>();
    badgeSeverity = input<BadgeSeverity>(BadgeSeverity.Primary);
    ariaLabel = input<string | undefined>();
    autofocus = input<boolean>(false);
    fluid = input<boolean>(false);
    buttonProps = input<unknown>();
    buttonVariant = input<ButtonVariant>(ButtonVariant.Default);
    buttonSize = input<ButtonSize>(ButtonSize.Normal);
    buttonIconPosition = input<ButtonIconPosition>(ButtonIconPosition.Left);
    buttonSeverity = input<ButtonSeverity>(ButtonSeverity.Primary);

    outlined = computed(() => this.buttonVariant() === ButtonVariant.Outlined);
    text = computed(() => this.buttonVariant() === ButtonVariant.Text);
    linkAppearance = computed(() => this.link() || this.buttonVariant() === ButtonVariant.Link);
    shouldApplyRaised = computed(() => {
        const isTextVariant = this.buttonVariant() === ButtonVariant.Text;
        const isNotPrimarySeverity = this.buttonSeverity() !== ButtonSeverity.Primary;

        return this.raised() && isTextVariant && isNotPrimarySeverity;
    });

    onClick = output<MouseEvent>();
    onFocus = output<FocusEvent>();
    onBlur = output<FocusEvent>();
}

export enum ButtonIconPosition {
    Left = 'left',
    Right = 'right'
}

export enum ButtonSeverity {
    Primary = 'primary',
    Secondary = 'secondary',
    Tertiary = 'help',
    Danger = 'danger'
}

export enum ButtonSize {
    Small = 'small',
    Normal = null,
    Large = 'large'
}

export enum ButtonVariant {
    Default = null,
    Outlined = 'outlined',
    Text = 'text',
    Link = 'link'
}

export enum ButtonType {
    Button = 'button',
    Submit = 'submit',
    Reset = 'reset'
}
